<script>
import ModelChildIndex from "../../models/children/index";

export default {
  extends: ModelChildIndex,
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t("trips.plural"));
      }
    },
    resourceRoute: { default: "trips" },
    modelVariable: { default: "trip" },
    tableMinHeight: { type: Number, default: 85.5 }, // 42.5 + 43
    tableHeight: { type: Number, default: 214.5 }, // 42.5 + (43 * 4)
    denyCreate: { type: Boolean, default: true },
    hideTableHeader: { type: Boolean, default: true },
    values: {
      type: Array,
      default() {
        return [];
      }
    },
    showProps: {
      default() {
        return {
          cardTitle: this.lodash.startCase(this.$t("trips.singular")),
          resourceRoute: { default: "trips" },
          modelVariable: "trip",
          modelMapped: require("@/router/views/trips/show.vue").default.computed.modelMapped.bind(this),
          denyEdit: true,
          denyDelete: true
        };
      }
    }
  },
  data() {
    return {
      sortBy: "ordered_at",
      sortDesc: true
    };
  },
  computed: {
    columns() {
      return {
        ["trip"]: [
          {
            key: "id",
            label: this.lodash.startCase(this.$t("tickets.trip_id")),
            thStyle: { minWidth: "125px" },
            component: {
              props: ["item"],
              template: '<router-link :to="`/trips/${item.id}`">{{ `${item.id }` }}</router-link>'
            }
          },
          {
            key: "trip_items.purchase_item.purchase_order_id",
            label: this.lodash.startCase(this.$t(`purchase_payments.purchase_order_id`)),
            thStyle: { minWidth: "100px" },
            component: {
              props: ["item"],
              template:
                '<div><template v-for="(purchaseOrder, index) in item.purchase_orders"><router-link :to="`/purchase_orders/${purchaseOrder.id}`">{{ `${purchaseOrder.id}` }}</router-link><span v-if="index !== (item.purchase_orders || []).length - 1">, </span></template></div>'
            }
          },
          {
            key: "tripped_at",
            label: this.lodash.startCase(this.$t("trips.tripped_at")),
            thStyle: { minWidth: "125px" },
            component: {
              props: ["item"],
              template:
                '<div v-if="item.tripped_at" style="width: 125px" class="text-truncate" :title="item.tripped_at">{{ $moment.utc(item.tripped_at).local().format(`DD MMM YYYY`) }}</div>' +
                "<div v-else>-</div>"
            }
          },
          {
            key: "trip_items.purchase_item.purchase_order.customer.name",
            label: this.lodash.startCase(this.$t(`trips.partner`)),
            thStyle: { minWidth: "100px" },
            component: {
              props: ["item"],
              template:
                '<div><template v-for="(purchaseOrder, index) in item.purchase_orders"><router-link :to="`/customers/${purchaseOrder.customer.id}`">{{ `${purchaseOrder.customer.name}` }}</router-link><span v-if="index !== (item.purchase_orders || []).length - 1">, </span></template></div>'
            }
          },
          {
            key: "customer.name",
            label: this.lodash.startCase(this.$t("trips.factory_destination")),
            component: {
              props: ["item"],
              methods: {
                format({ type, customer }) {
                  if (type === "hybrid") return "-";
                  return customer.name ?? "-";
                }
              },
              template: "<span>{{format(item)}}</span>"
            }
          }
        ]
      };
    }
  },
  methods: {
    store(value) {
      return this.$axios.post(`/tickets/${this.$route.params.key}/trips`, value);
    },
    update(value) {
      return this.$axios.put(`/tickets/${this.$route.params.key}/trips/${this.getModelKey(value)}`, value);
    },
    delete(value) {
      return this.$axios.delete(`/tickets/${this.$route.params.key}/trips/${this.getModelKey(value)}`);
    },
    fetchModels(page = 1) {
      let vm = this;
      return this.$axios
        .get(`/trips?ticket_id__eq=${this.$route.params.key}`, {
          params: {
            page: page,
            per_page: this.perPage,
            search: this.search,
            sort: `${this.sortBy},${this.sortDesc ? "desc" : "asc"}`,
            with_sub_product: "1",
            with: [
              "customer",
              "trip_items.purchase_order.purchase_items",
              "trip_items.purchase_order.ticket",
              "trip_items.purchase_order.customer",
              "vehicle",
              "driver",
              "creator",
              "sale_order"
            ].join(","),
            ...this.filterParams
          }
        })
        .then(response => {
          const {
            data: { data, meta }
          } = response;
          this.models = data.map(function(datum) {
            let purchaseOrders = [];
            datum.trip_items.forEach(tripItem => {
              let purchaseOrderIds = purchaseOrders.map(purchaseOrder => purchaseOrder.id);
              if (!purchaseOrderIds.includes(tripItem.purchase_order.id)) {
                purchaseOrders.push(tripItem.purchase_order);
              }
            });
            let purchaseOrder = datum.trip_items.length
              ? vm.lodash
                  .uniq(
                    datum.trip_items.map(function(item) {
                      let po = item.purchase_order;
                      return `${po.id} (${po.customer.name} - ${vm.$moment
                        .utc(po.ordered_at)
                        .local()
                        .format("DD MMM YYYY")})`;
                    })
                  )
                  .join(", ")
              : "";
            let purchaseItems = datum.trip_items.length
              ? vm.lodash.uniqBy(vm.lodash.flatten(datum.trip_items.map(i => i.purchase_order.purchase_items)), "id")
              : [];
            let total_purchase_items = purchaseItems.length
              ? purchaseItems.reduce(
                  (total, v) => total + (v.quantity - v.trip_item_total_quantity - v.sale_item_total_quantity || 0),
                  0
                )
              : 0;
            let total_trip_items = datum.trip_items.length
              ? datum.trip_items.reduce((total, v) => total + (v.quantity || 0), 0)
              : 0;

            return {
              ...datum,
              ticket: datum.trip_items.length ? datum.trip_items[0].purchase_order.ticket : null,
              purchase_order: purchaseOrder,
              purchase_orders: purchaseOrders,
              total_purchase_items: total_purchase_items,
              total_trip_items: total_trip_items,
              total_difference: total_purchase_items - total_trip_items
            };
          });
          this.page = Number(meta.current_page);
          this.perPage = Number(meta.per_page);
          this.from = Number(meta.from);
          this.total = Number(meta.total);

          this.stats = [];

          return response;
        })
        .catch(error => {
          this.failedAlert(error);
          return null;
        });
    }
  }
};
</script>
